import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { TrendingDown } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';

export interface BurndownPoint {
  date: string;
  ideal: number;
  remaining: number;
}

export interface BurndownWidgetProps {
  data?: readonly BurndownPoint[];
  className?: string;
  testId?: string;
  loading?: boolean;
}

export function BurndownWidget({
  data = [],
  className,
  testId,
  loading = false,
}: BurndownWidgetProps): ReactElement {
  const { t } = useTranslation();

  const widgetContainerProps = {
    title: t('crm.dashboard.burndown.title'),
    icon: TrendingDown,
    iconColor: 'var(--ant-color-primary)', // warning amber
    minHeight: 220,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div
          className="animate-pulse flex items-center justify-center crm-radius-s h-[170px]"
          style={{ background: 'var(--g-fill, #f5f1eb)' }}
        />
      ) : data.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <TrendingDown size={16} style={{ color: 'var(--ant-color-text-tertiary)' }} />
          <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}>
            {t('crm.dashboard.burndown.empty')}
          </span>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={170}>
          <AreaChart data={data}>
            <defs>
              <linearGradient id="burnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--ant-color-primary-border-hover, #d97706)"
                  stopOpacity={0.25}
                />
                <stop
                  offset="95%"
                  stopColor="var(--ant-color-primary-border-hover, #d97706)"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--ant-color-border)" opacity={0.6} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 9, fill: 'var(--ant-color-text-secondary)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 9, fill: 'var(--ant-color-text-secondary)' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--ant-color-bg-elevated)',
                border: '1px solid var(--ant-color-border-secondary)',
                borderRadius: 6,
                fontSize: 11,
              }}
            />
            <Area
              type="monotone"
              dataKey="ideal"
              stroke="var(--ant-color-text-quaternary, #a8a29e)"
              strokeWidth={1}
              strokeDasharray="4 4"
              fill="none"
              name={t('crm.dashboard.burndown.ideal')}
            />
            <Area
              type="monotone"
              dataKey="remaining"
              stroke="var(--ant-color-primary-border-hover, #d97706)"
              strokeWidth={2}
              fill="url(#burnGrad)"
              name={t('crm.dashboard.burndown.remaining')}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </WidgetContainer>
  );
}
